import { useEffect, useState } from "react";
import { Loader2, Award, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { getComparisonRecommendation, trackComparisonClick } from "@/lib/api";
import ProviderGrid from "@/components/ProviderGrid";

export default function ComparisonSection({ analysisId, possibleSavings = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await getComparisonRecommendation({ analysis_id: analysisId });
        setData(r);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [analysisId]);

  const handleClick = async (providerId) => {
    try {
      const r = await trackComparisonClick({ provider_id: providerId, analysis_id: analysisId });
      if (r.url) {
        window.open(r.url, "_blank", "noopener,noreferrer");
      }
    } catch {
      toast.error("Doorsturen mislukt — probeer opnieuw.");
    }
  };

  if (loading) {
    return (
      <section className="mt-12" data-testid="comparison-section-loading">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Beste aanbieders berekenen...
        </div>
      </section>
    );
  }

  if (!data?.top?.length) return null;

  return (
    <section className="mt-12" data-testid="comparison-section">
      <div className="mb-6">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full">
          <Sparkles className="h-3.5 w-3.5" />
          Persoonlijk advies
        </span>
        <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Beste aanbieders voor jouw situatie
        </h2>
        <p className="mt-2 text-slate-600 max-w-2xl">
          Op basis van je verbruik, contracttype en zonnepanelen hebben we de drie best passende leveranciers voor je geselecteerd.
        </p>
      </div>

      {/* ACM trust banner */}
      <div className="mb-6 bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-100 rounded-xl px-5 py-3 flex flex-wrap items-center gap-3" data-testid="acm-trust-banner">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-emerald-600 text-white grid place-items-center">
            <Award className="h-4 w-4" />
          </div>
          <div>
            <div className="font-heading font-semibold text-slate-900 text-sm">ACM Keurmerk</div>
            <div className="text-[11px] text-slate-600">Geschillencommissie Energie · Transparante tarieven</div>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-1.5 text-[10px]">
          {["Geen verborgen kosten", "Onafhankelijk advies", "9 aanbieders"].map((b, i) => (
            <span key={i} className="bg-white border border-slate-200 px-2 py-0.5 rounded-full text-slate-700">
              {b}
            </span>
          ))}
        </div>
      </div>

      <ProviderGrid
        top={data.top}
        all={data.all}
        onProviderClick={handleClick}
        possibleSavings={possibleSavings}
      />
    </section>
  );
}
